import { useEffect, useRef } from 'react';
import { instance } from 'api/api';
import { FormRendererAPIType } from 'components/common/form';

export interface DomainKgStatusPayload {
    concierge_kg_status: string;
    concierge_kg_profiled_time: string | null;
    concierge_kg_active_run_uid: string | null;
    concierge_kg_open_item_count?: number | null;
    concierge_kg_publish_blocked?: boolean;
    concierge_kg_can_publish?: boolean;
    concierge_kg_last_run_status?: string | null;
    concierge_kg_last_run_error?: string | null;
    concierge_kg_last_run_time?: string | null;
}

export const domainKgStatusUrl = (domainId: string) => `/data/admin/domain/${domainId}/concierge-kg`;

/** Re-read the KG fields when a row leaves the grid: the server unpublished the model, the form holds the old pill. */
export function useDomainKgStatusRefresh(
    domainId: string | undefined,
    gridRows: unknown[] | undefined,
    form: Pick<FormRendererAPIType, 'hookFormSetValue'>,
): void {
    const rowCountRef = useRef<number | undefined>(undefined);
    const formRef = useRef(form);
    formRef.current = form;
    const rowCount = gridRows?.length;

    useEffect(() => {
        if (rowCount === undefined) {
            return;
        }
        const previous = rowCountRef.current;
        rowCountRef.current = rowCount;
        if (previous === undefined || rowCount >= previous || !domainId || !/^\d+$/.test(domainId)) {
            return;
        }
        instance
            .get<{ status: string; data: DomainKgStatusPayload }>(domainKgStatusUrl(domainId))
            .then((response) => {
                if (response.data.status !== 'OK') {
                    return;
                }
                const data = response.data.data;
                const fields: Record<string, string | number | boolean | null> = {
                    concierge_kg_status: data.concierge_kg_status,
                    concierge_kg_profiled_time: data.concierge_kg_profiled_time,
                    concierge_kg_active_run_uid: data.concierge_kg_active_run_uid,
                    concierge_kg_open_item_count: data.concierge_kg_open_item_count ?? null,
                    concierge_kg_publish_blocked: data.concierge_kg_publish_blocked ?? false,
                    concierge_kg_can_publish: data.concierge_kg_can_publish ?? true,
                    concierge_kg_last_run_status: data.concierge_kg_last_run_status ?? null,
                    concierge_kg_last_run_error: data.concierge_kg_last_run_error ?? null,
                    concierge_kg_last_run_time: data.concierge_kg_last_run_time ?? null,
                };
                Object.entries(fields).forEach(([field, value]) =>
                    formRef.current.hookFormSetValue(field, value, { shouldDirty: false }),
                );
            })
            .catch(() => undefined);
    }, [rowCount, domainId]);
}
